import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PaymentLogos } from "@/components/PaymentTrust";

const DEEP = "#1E40AF";
const MUTED = "#C7D2F5";

// Fixed layout; each footer-type page shows only while it's active. Core legal pages always show.
const LAYOUT = [
  { heading: "Buy", links: [["how-it-works", "How It Works"], ["pricing", "Pricing"], ["returns", "Returns"], ["help", "Help"]] },
  { heading: "Sell", links: [["@sell", "Sell with AI", "/sell"], ["seller-guide", "Seller Guide"]] },
  { heading: "Company", links: [["about", "About"], ["contact", "Contact"]] },
  { heading: "Legal", links: [["terms", "Terms & Conditions"], ["refunds", "Refund Policy"], ["delivery", "Delivery Policy"], ["privacy", "Privacy Policy"], ["prohibited-items", "Prohibited Items"], ["counterfeit-policy", "Counterfeit Policy"]] },
];
const ALWAYS = new Set(["@sell", "terms", "refunds", "privacy"]);

function FLink({ label, to }) {
  const cls = "text-[13px] transition-colors hover:text-white";
  const testid = `footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
  if (to.startsWith("/")) return <Link to={to} data-testid={testid} className={cls} style={{ color: MUTED }}>{label}</Link>;
  return <a href={to} data-testid={testid} className={cls} style={{ color: MUTED }}>{label}</a>;
}

export default function Footer() {
  const [legal, setLegal] = useState({});
  const [footerPages, setFooterPages] = useState([]);

  useEffect(() => {
    api.get("/legal").then(({ data }) => {
      const m = {}; data.forEach((d) => { m[d.page_key] = d; }); setLegal(m);
    }).catch(() => {});
    api.get("/footer-pages").then(({ data }) => setFooterPages(data)).catch(() => {});
  }, []);

  const pages = { ...Object.fromEntries(footerPages.map((p) => [p.page_key, p])), ...legal };
  const columns = LAYOUT.map((c) => ({
    heading: c.heading,
    links: c.links
      .filter(([k]) => ALWAYS.has(k) || pages[k]?.active)
      .map(([k, label, path]) => [pages[k]?.footer_link_text || label, path || pages[k]?.path || `/${k}`]),
  }));

  return (
    <footer data-testid="site-footer" className="mt-16 text-white" style={{ backgroundColor: DEEP }}>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
          <div className="col-span-2 md:col-span-1">
            <img src="/logo.png" alt="SellStuff" className="h-[39px] w-auto" />
            <p className="mt-2 text-sm font-medium text-white">Sell Smarter. Buy Safer.</p>
            <p className="mt-1 text-[13px]" style={{ color: MUTED }}>AI-Powered Second-Hand Marketplace · Dubai, UAE</p>
            <div className="mt-4 flex items-center gap-3" data-testid="footer-social">
              <a href="https://www.instagram.com/sellstuff.ai/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="text-white transition-opacity hover:opacity-70">
                <i className="ti ti-brand-instagram" style={{ fontSize: "22px" }} />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61592656861808" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="text-white transition-opacity hover:opacity-70">
                <i className="ti ti-brand-facebook" style={{ fontSize: "22px" }} />
              </a>
            </div>
          </div>
          {columns.filter((c) => c.links.length > 0).map((c) => (
            <div key={c.heading} data-testid={`footer-col-${c.heading.toLowerCase()}`}>
              <h4 className="text-sm font-medium text-white">{c.heading}</h4>
              <ul className="mt-3 space-y-2">
                {c.links.map(([label, to]) => <li key={to}><FLink label={label} to={to} /></li>)}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 border-t border-white/20 pt-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px]" style={{ color: MUTED }}>© 2026 SellStuff — Rolmn E-Commerce LLC. All Rights Reserved.</p>
            <p className="text-[11px]" style={{ color: MUTED }}>Payment Held Until You Receive It · 24 Hours to Raise a Concern · Delivered Across Dubai</p>
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid="footer-trust">
            <p className="text-[11px]" style={{ color: MUTED }} data-testid="footer-licence">Rolmn E-Commerce LLC · Licensed by Shams Media City, licence 1701308.01</p>
            <PaymentLogos dark />
          </div>
        </div>
      </div>
    </footer>
  );
}
